// @flow

import { addParamsToUrl } from '@zapier/url-utils';
import { utils as serviceUtils } from '@zapier/services';
import {
  convertToHeadAndTailActions,
  generateZapTitleFromActions,
  generateZapTitleFromQuery,
} from '@zapier/editor/src/utils';

import type {
  FlattenedState,
  SharedZapType,
  ZapTemplate,
} from '@zapier/editor/src/types';
import type { ServiceFromSSR as Service } from '@zapier/services';

type BuildNewZapUrlFromQueryProps = {
  ...$Exact<FlattenedState>,
  allowEmptySteps?: boolean,
};

type CreateZapProps = {
  /**
   ```
   referrer?: string,
   zapTitle?: ?string,
   fromNouns?: boolean,

   headServiceId: ?string,
   headActionId: ?string,
   headActionType: ?string,
   headActionLabel: ?string,

   tailServiceId: ?string,
   tailActionId: ?string,
   tailActionType: ?string,
   tailActionLabel: ?string,
   ```
   */
  query?: FlattenedState,
  /** Tells us where the Zap was created from. */
  referrer?: string,
  /** Need this for generating a Zap title. */
  services?: Service[],
  /**
   ```
   id: number,
   title: string,
   descriptionHtml: string,
   labels: string[],
   headService: {
    id: string,
    name: string,
  },
   tailService: {
    id: string,
    name: string,
  },
   url: string,
   ```
   */
  zt?: ZapTemplate | SharedZapType,
};

export const buildNewZapUrlFromQuery = ({
  referrer,
  zapTitle,
  allowEmptySteps = false,

  headServiceId,
  headActionId,
  headActionType,

  tailServiceId,
  tailActionId,
  tailActionType,
}: BuildNewZapUrlFromQueryProps = {}) => {
  const BASE_URL = referrer
    ? `/app/editor?referrer=${referrer}&create=true`
    : `/app/editor?create=true`;

  if (!headServiceId && !allowEmptySteps) {
    return BASE_URL;
  }

  return [
    `${BASE_URL}`,
    headServiceId ? `template__0__selected_api=${headServiceId}` : null,
    headActionType ? `template__0__type_of=${headActionType}` : null,
    headActionId ? `template__0__action=${headActionId}` : null,

    tailServiceId ? `template__1__selected_api=${tailServiceId}` : null,
    (tailServiceId || allowEmptySteps) && tailActionType
      ? `template__1__type_of=${tailActionType}`
      : null,
    tailServiceId && tailActionId
      ? `template__1__action=${tailActionId}`
      : null,
    // To create a Zap with the title set (currently only used by Simple Zap Setup)
    zapTitle ? `template__0__title=${encodeURIComponent(zapTitle)}` : null,
  ]
    .filter(Boolean)
    .join('&');
};

const getHrefForBlankZap = ({ query, referrer }) => {
  return {
    href: buildNewZapUrlFromQuery({ ...query, referrer }),
    type: 'blank',
  };
};

const getHrefForZts = (props: CreateZapProps) => {
  const { zt, referrer, services } = props;

  if (!zt) {
    return getHrefForBlankZap(props);
  }

  const serviceIds = serviceUtils.getServiceIds(services);

  return {
    href: addParamsToUrl(
      `/app/editor/template/${zt.id}`,
      {
        selected_apis: (serviceIds || []).join(','),
        referrer,
      },
      true
    ),
    type: 'template',
  };
};

const getHrefFromOutline = (props: CreateZapProps) => {
  const { query, referrer, services = [] } = props;

  const generateZapTitle =
    query && query.headServiceName
      ? generateZapTitleFromQuery
      : generateZapTitleFromActions;

  const zapTitle = generateZapTitle({
    query,
    services,
  });

  const flattenedState = {
    zapTitle,
    referrer,
    ...query,
  };

  return {
    href: buildNewZapUrlFromQuery(flattenedState),
    type: 'outline',
    zapTitle,
  };
};

const buildNewZapUrl = (props: CreateZapProps) => {
  const { zt, query, referrer } = props;
  const { headAction, tailAction } = convertToHeadAndTailActions(query);
  const queryIsEmpty = query && Object.keys(query).every(key => !query[key]);

  if (!zt && (!query || queryIsEmpty)) {
    return getHrefForBlankZap(props);
  }

  if (zt) {
    return getHrefForZts(props);
  }

  const headActionChosen = headAction && headAction.serviceId && headAction.id;
  const tailActionChosen = tailAction && tailAction.serviceId && tailAction.id;

  // This is when some of the apps / actions are chosen (not all supplied)
  if (!(headActionChosen && tailActionChosen)) {
    return {
      href: buildNewZapUrlFromQuery({ ...query, referrer }),
      type: 'partial',
    };
  }

  return getHrefFromOutline(props);
};

export default buildNewZapUrl;
